"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { useSession, signOut } from "next-auth/react";
import Image from "next/image";
import Styles from "./Navbar.module.css";
import { FaBars, FaTimes, FaCaretDown, FaUser, FaSignOutAlt } from "react-icons/fa";
import NavbarItems from "./NavbarItems";
import ThemeToggle from "@/components/ui/ThemeToggle";
import { useTheme } from "@teispace/next-themes";

export default function NavBar() {
  const [showMobileNav, setShowMobileNav] = useState(false);
  const [activeDesktopDropdown, setActiveDesktopDropdown] = useState(null);
  const [mobileDropdownState, setMobileDropdownState] = useState({});
  const [isScrolled, setIsScrolled] = useState(false);
  const closeTimeoutRef = useRef(null);
  const navRef = useRef(null);

  const { theme, resolvedTheme, setTheme } = useTheme();
  const { data: session, status } = useSession();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const profileImageLoader = ({ src }) => src;

  const currentSearchParams = searchParams.toString();
  const fullCurrentPath = currentSearchParams ? `${pathname}?${currentSearchParams}` : pathname;
  const encodedReturnUrl = encodeURIComponent(fullCurrentPath);

  const loginHref = pathname === '/login'
    ? (currentSearchParams ? `/login?${currentSearchParams}` : '/login')
    : `/login?returnUrl=${encodedReturnUrl}`;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const openDesktopDropdown = (key) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setActiveDesktopDropdown(key);
  };

  const closeDesktopDropdownWithDelay = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    closeTimeoutRef.current = setTimeout(() => {
      setActiveDesktopDropdown(null);
    }, 150);
  };

  const closeDesktopDropdownImmediately = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setActiveDesktopDropdown(null);
  };

  const toggleDesktopDropdown = (key) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setActiveDesktopDropdown((prev) => (prev === key ? null : key));
  };

  const toggleMobileNav = () => {
    setShowMobileNav(!showMobileNav);
  };

  const toggleMobileDropdown = (index) => {
    setMobileDropdownState((prevState) => ({
      ...prevState,
      [index]: !prevState[index],
    }));
  };

  const closeMobileNav = () => {
    setShowMobileNav(false);
    setMobileDropdownState({});
  };

  // Close desktop and mobile menus on route change during render
  const [prevPath, setPrevPath] = useState(fullCurrentPath);
  if (prevPath !== fullCurrentPath) {
    setPrevPath(fullCurrentPath);
    if (activeDesktopDropdown !== null) {
      setActiveDesktopDropdown(null);
    }
    if (showMobileNav) {
      setShowMobileNav(false);
    }
    if (Object.keys(mobileDropdownState).length > 0) {
      setMobileDropdownState({});
    }
  }

  // Handle escape key and click outside for desktop dropdown
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        closeDesktopDropdownImmediately();
        if (showMobileNav) {
          closeMobileNav();
        }
      }
    };

    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        closeDesktopDropdownImmediately();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleClickOutside);
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, [showMobileNav]);



  // Add this new useEffect to lock body scroll when menu is open
  useEffect(() => {
    if (showMobileNav) {
      // Lock scroll and prevent address bar from shifting
      document.body.style.overflow = 'hidden';
    } else {
      // Restore scroll when closed
      document.body.style.overflow = 'unset';
    }

    // Cleanup function in case component unmounts while menu is open
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showMobileNav]);

  return (
    <nav
      ref={navRef}
      className={`${Styles.navbar} ${isScrolled ? Styles.navbarScrolled : ''}`}
      role="navigation"
      aria-label="Main navigation"
    >
      {/* Logo */}
      <div className={Styles.navbarLogo}>
        <Link href="/" aria-label="Go to homepage">
          JEE Challenger
        </Link>
      </div>

      {/* Desktop Navigation */}
      <ul className={Styles.navbarLinks} role="menubar">
        {NavbarItems.map((item, index) => {
          if (item.type === "link") {
            return (
              <li key={index} className={Styles.li} role="none">
                <Link
                  href={item.url}
                  role="menuitem"
                  aria-label={`Go to ${item.title} page`}
                  onClick={closeDesktopDropdownImmediately}
                >
                  {item.title}
                </Link>
              </li>
            );
          } else if (item.type === "dropdown") {
            const isOpen = activeDesktopDropdown === index;
            return (
              <li
                key={index}
                className={Styles.li}
                role="none"
                onMouseEnter={() => openDesktopDropdown(index)}
                onMouseLeave={closeDesktopDropdownWithDelay}
              >
                <button
                  className={Styles.columnsDropdown}
                  onClick={() => toggleDesktopDropdown(index)}
                  aria-expanded={isOpen}
                  aria-haspopup="true"
                  aria-label={`Open dropdown for ${item.title} options`}
                  role="menuitem"
                >
                  {item.title}
                  <span
                    className={Styles.columnsDropdownArrow}
                    style={{
                      transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                  >
                    <FaCaretDown />
                  </span>
                </button>
                <div
                  className={Styles.columnsDropdownContent}
                  style={{
                    opacity: isOpen ? 1 : 0,
                    visibility: isOpen ? 'visible' : 'hidden',
                    transform: isOpen ? 'translateY(0) scale(1)' : 'translateY(-10px) scale(0.95)'
                  }}
                  role="menu"
                  aria-label={`${item.title} submenu`}
                >
                  <ul>
                    {item.items.map((subitem, subindex) => (
                      <li key={subindex} className={Styles.columnsDropdownContentli} role="none">
                        <Link
                          href={subitem.url}
                          role="menuitem"
                          aria-label={`Go to ${subitem.title} page`}
                          onClick={closeDesktopDropdownImmediately}
                        >
                          {subitem.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </li>
            );
          }
          return null;
        })}
        <li className={Styles.li} role="none">
          <ThemeToggle />
        </li>
        {/* User Auth Section */}
        <li className={Styles.li} role="none">
          {status === "loading" ? (
            <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse"></div>
          ) : status === "authenticated" ? (
            (() => {
              const isUserOpen = activeDesktopDropdown === 'user';
              return (
                <div
                  className={Styles.li}
                  role="none"
                  onMouseEnter={() => openDesktopDropdown('user')}
                  onMouseLeave={closeDesktopDropdownWithDelay}
                >
                  <button
                    className={Styles.columnsDropdown}
                    onClick={() => toggleDesktopDropdown('user')}
                    aria-expanded={isUserOpen}
                    aria-haspopup="true"
                    aria-label="Open user menu"
                    role="menuitem"
                    style={{ padding: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                  >
                    {session?.user?.image ? (
                      <Image
                        src={session.user.image}
                        alt={session.user.name || "User"}
                        width={32}
                        height={32}
                        loader={profileImageLoader}
                        unoptimized
                        className="w-8 h-8 rounded-full border border-gray-300 dark:border-gray-600 object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center border border-blue-200 dark:border-blue-800">
                        <FaUser className="text-blue-600 dark:text-blue-400" />
                      </div>
                    )}
                    <span
                      className={Styles.columnsDropdownArrow}
                      style={{
                        transform: isUserOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      }}
                    >
                      <FaCaretDown />
                    </span>
                  </button>
                  <div
                    className={Styles.columnsDropdownContent}
                    style={{
                      opacity: isUserOpen ? 1 : 0,
                      visibility: isUserOpen ? 'visible' : 'hidden',
                      transform: isUserOpen ? 'translateY(0) scale(1)' : 'translateY(-10px) scale(0.95)',
                      right: 0,
                      left: 'auto',
                      minWidth: '150px'
                    }}
                    role="menu"
                    aria-label="User account menu"
                  >
                    <ul>
                      <li className={Styles.columnsDropdownContentli} role="none">
                        <Link 
                          href={`/profile${pathname === '/' ? '' : `?returnUrl=${encodedReturnUrl}`}`}
                          onClick={closeDesktopDropdownImmediately}
                          role="menuitem"
                        >
                          Profile
                        </Link>
                      </li>
                      <li className={Styles.columnsDropdownContentli} role="none">
                        <button
                          onClick={() => {
                            closeDesktopDropdownImmediately();
                            signOut({ callbackUrl: fullCurrentPath });
                          }}
                          className={Styles.signOutDesktop}
                          role="menuitem"
                        >
                          <FaSignOutAlt />
                          Sign Out
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              );
            })()
          ) : (
            <Link
              href={loginHref}
              className="!bg-blue-600 hover:!bg-blue-700 !text-white px-4 py-2 rounded-lg font-medium transition-colors text-sm"
              style={{ display: 'inline-block' }}
            >
              Sign In
            </Link>
          )}
        </li>
      </ul>

      {/* Mobile Navigation Controls */}
      <div className="flex lg:hidden items-center gap-4">
        <button
          className={Styles.hamburger}
          onClick={toggleMobileNav}
          aria-expanded={showMobileNav}
          aria-label={showMobileNav ? 'Close navigation menu' : 'Open navigation menu'}
          aria-controls="mobile-menu"
        >
          <div className={Styles.hamburgerIcon}>
            <FaBars />
          </div>
        </button>
      </div>

      {/* Mobile Navigation Menu */}
      <div
        id="mobile-menu"
        className={Styles.mobileNav}
        style={{ width: showMobileNav ? '100vw' : '0' }}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation menu"
      >
        <div className={Styles.mobileNavContainer}>
          <button
            className={Styles.mobileCloseButton}
            onClick={closeMobileNav}
            aria-label="Close navigation menu"
          >
            <FaTimes />
          </button>
          <ul className={Styles.mobileNavLinks} role="menu">
            {NavbarItems.map((item, index) => {
              if (item.type === 'link') {
                return (
                  <li key={index} role="none">
                    <Link
                      href={item.url}
                      onClick={closeMobileNav}
                      role="menuitem"
                      aria-label={`Go to ${item.title} page`}
                    >
                      {item.icon}
                      {item.title}
                    </Link>
                  </li>
                );
              } else if (item.type === 'dropdown') {
                const isMobileOpen = Boolean(mobileDropdownState[index]);
                return (
                  <li key={index} className={Styles.mobileDropdown} role="none">
                    <button
                      onClick={(event) => {
                        event.preventDefault();
                        toggleMobileDropdown(index);
                      }}
                      aria-expanded={isMobileOpen}
                      aria-haspopup="true"
                      aria-label={`Open dropdown for ${item.title} options`}
                      role="menuitem"
                      className={Styles.mobileDropdownButton}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        {item.icon}
                        {item.title}
                      </span>
                      <span
                        className={Styles.mobileDropdownArrow}
                        style={{
                          transform: isMobileOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        }}
                      >
                        <FaCaretDown />
                      </span>
                    </button>
                    <div
                      className={Styles.columnsDropdownMobile}
                      style={{ maxHeight: isMobileOpen ? '500px' : '0' }}
                      role="menu"
                      aria-label={`${item.title} submenu`}
                    >
                      <ul>
                        {item.items.map((subitem, subindex) => (
                          <li key={subindex} role="none">
                            <Link
                              href={subitem.url}
                              onClick={closeMobileNav}
                              role="menuitem"
                              aria-label={`Go to ${subitem.title} page`}
                            >
                              {subitem.title}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </li>
                );
              }
              return null;
            })}
            {status === "loading" ? null : status === "authenticated" ? (
              <>
                <li role="none">
                  <Link href={`/profile${pathname === '/' ? '' : `?returnUrl=${encodedReturnUrl}`}`} onClick={closeMobileNav} role="menuitem">
                    <FaUser />
                    Profile
                  </Link>
                </li>
                <li role="none">
                  <button
                    onClick={() => { closeMobileNav(); signOut({ callbackUrl: fullCurrentPath }); }}
                    role="menuitem"
                    className={Styles.signOutMobile}
                  >
                    <FaSignOutAlt />
                    Sign Out
                  </button>
                </li>
              </>
            ) : (
              <li role="none">
                <Link href={loginHref} onClick={closeMobileNav} role="menuitem">
                  <FaUser />
                  Sign In
                </Link>
              </li>
            )}
            <li className={Styles.mobileThemeToggle} role="none">
              <div
                className={Styles.mobileThemeToggleContainer}
                onClick={() => {
                  // Toggle theme directly using the useTheme hook
                  const current = resolvedTheme ?? theme;
                  setTheme(current === 'dark' ? 'light' : 'dark');
                }}
              >
                <div
                  className={Styles.mobileThemeToggleIcon}
                  onClick={(e) => {
                    // Prevent the icon click from bubbling up to avoid double toggle
                    e.stopPropagation();
                  }}
                >
                  <ThemeToggle />
                </div>
                <div className={Styles.mobileThemeToggleContent}>
                  <span className={Styles.mobileThemeToggleLabel} suppressHydrationWarning>
                    {(resolvedTheme ?? theme)
                      ? ((resolvedTheme ?? theme) === 'dark'
                        ? 'Change to Light Mode'
                        : 'Change to Dark Mode')
                      : 'Change Theme'}
                  </span>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  );
}
